/**
 * 認証済みユーザー向けパスワード API（FE 先行定義）。
 *
 * - GET  /v2/auth/password/policy … 所属 org 横断の実効ポリシー + description
 * - POST /v2/auth/password        … パスワード設定・変更（AAL1 freshness）
 *
 * NestJS SharedController（非 oRPC）だが、OpenAPILink は path/method で REST 呼び出し可能なため
 * ローカル Contract として扱う。OpenAPI schema / api-contract publish 後に差し替え可。
 *
 * BE の `DressCodeApiController` は `Controller({ version: '2' })` で URI バージョニングを行うため、
 * コントローラーが `@Get('/auth/password/policy')` でも実 URL は `/v2` 配下になる
 * （openspec のドキュメントは `/v2` 無しで記載されているが誤り）。
 */
import { oc } from '@orpc/contract';
import { z } from 'zod';
export const EffectivePasswordPolicySchema = z.object({
  minLength: z.number().int().min(1),
  requireUppercase: z.boolean(),
  requireLowercase: z.boolean(),
  requireNumber: z.boolean(),
  requireSymbol: z.boolean(),
  leakedPasswordCheckEnabled: z.boolean(),
  /** リクエストロケールに対応した要件説明文。 */
  description: z.string()
});
export const UpdatePasswordInputSchema = z.object({
  newPassword: z.string().min(1)
});
export const authPasswordContract = oc.router({
  getEffectivePolicy: oc.route({
    method: 'GET',
    path: '/v2/auth/password/policy'
  }).input(z.object({})).output(EffectivePasswordPolicySchema),
  updatePassword: oc.route({
    method: 'POST',
    path: '/v2/auth/password'
  }).input(UpdatePasswordInputSchema).output(z.void())
});
export type EffectivePasswordPolicy = z.infer<typeof EffectivePasswordPolicySchema>;

/**
 * 未認証導線（招待登録・Ory new-password 等）向けのフォールバックポリシー。
 * 旧 PASSWORD_REGEX（10文字・大小英数記号）と同等。招待 API が effective policy を返すようになったら差し替える。
 */
export const DEFAULT_PASSWORD_POLICY = {
  minLength: 10,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSymbol: true
} as const satisfies Pick<EffectivePasswordPolicy, 'minLength' | 'requireUppercase' | 'requireLowercase' | 'requireNumber' | 'requireSymbol'>;
export type PasswordPolicyRequirements = Pick<EffectivePasswordPolicy, 'minLength' | 'requireUppercase' | 'requireLowercase' | 'requireNumber' | 'requireSymbol'>;

/** BE evaluator と同じ文字種判定（FE リアルタイムチェック用）。 */
export const PASSWORD_CHARACTER_CLASS_PATTERN = {
  uppercase: /[A-Z]/,
  lowercase: /[a-z]/,
  number: /[0-9]/,
  symbol: /[^a-zA-Z0-9]/
} as const;
export type PasswordRequirementCheck = {
  key: 'minLength' | 'uppercase' | 'lowercase' | 'number' | 'symbol';
  met: boolean;
};

/** 入力に対する要件チェックリスト（HIBP はサーバ側のみのため含めない）。 */
export function evaluatePasswordRequirements(password: string, policy: PasswordPolicyRequirements): PasswordRequirementCheck[] {
  const checks: PasswordRequirementCheck[] = [{
    key: 'minLength',
    met: password.length >= policy.minLength
  }];
  if (policy.requireUppercase) {
    checks.push({
      key: 'uppercase',
      met: PASSWORD_CHARACTER_CLASS_PATTERN.uppercase.test(password)
    });
  }
  if (policy.requireLowercase) {
    checks.push({
      key: 'lowercase',
      met: PASSWORD_CHARACTER_CLASS_PATTERN.lowercase.test(password)
    });
  }
  if (policy.requireNumber) {
    checks.push({
      key: 'number',
      met: PASSWORD_CHARACTER_CLASS_PATTERN.number.test(password)
    });
  }
  if (policy.requireSymbol) {
    checks.push({
      key: 'symbol',
      met: PASSWORD_CHARACTER_CLASS_PATTERN.symbol.test(password)
    });
  }
  return checks;
}
export function areAllPasswordRequirementsMet(password: string, policy: PasswordPolicyRequirements): boolean {
  return evaluatePasswordRequirements(password, policy).every(check => check.met);
}